import { useAuthState, type AuthState } from '@/app/providers/auth'
import { useLanguage } from '@/components/Link'
import type { BasketFragment } from '@/generated/graphql'
import { getCustomerFacingPriceItem } from '@/helpers/customerPricing/customerPricing'

import { parseISO, isValid as isValidDate } from 'date-fns'
import type { TFunction } from 'i18next'
import { useSearchParams } from 'next/navigation'
import React, { createContext, useContext, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { match } from 'ts-pattern'

import { validateUserBasketSettings } from '../../../common/domain/user-basket-settings'
import { useUserBasketSettingsState } from '../../../common/states/useUserBasketSettingsState'
import type { MenuVendor } from '../../domain/menu-vendor'
import { useMixpanelBasketCreated } from '../../hooks/useMixpanelBasketCreated'
import {
  type ItemAddSource,
  useMixpanelItemAddedToBasket,
} from '../../hooks/useMixpanelItemAddedToBasket'
import { useBasketSelection } from '../../states/basket-selection/useBasketSelection'
import type { AddItemPayload } from '../../states/menu-page/add-item-mutation'
import { useSelectedBasketState } from '../../states/selected-basket'
import { useExistingOrder } from '../../states/useExistingOrder'
import { useBasketStateStore } from '../../store/basketStateStore'
import { useCartModalActions } from '../../store/cartStore'
import { useVendorLocationData } from '../../store/vendorLocationStore'

// Types for the shared context
type SharedMenuItemCardState = {
  // Auth & account
  authState: AuthState
  account: { id: string; name: string } | null
  isEditingExistingOrder: boolean
  isEditingStandingOrder: boolean

  // Translation
  t: TFunction

  // Vendor location
  selectedVendorLocation: ReturnType<typeof useVendorLocationData>['selectedVendorLocation']

  // Basket state
  basketData: BasketFragment | null
  addItemToBasket: (basket: BasketFragment, payload: AddItemPayload) => void
  sharedAddingItem: boolean

  // Modal actions
  setOrderDetailsModalState: ReturnType<typeof useCartModalActions>['setOrderDetailsModalState']

  // Handlers that need vendor context
  handleCreateBasket: (vendor: MenuVendor, itemPayload?: AddItemPayload) => void
  handleAddItemToBasket: (vendor: MenuVendor, payload: AddItemPayload) => void
}

const MenuItemCardContext = createContext<SharedMenuItemCardState | null>(null)

export const useSharedMenuItemCardState = () => {
  const context = useContext(MenuItemCardContext)
  if (!context) {
    throw new Error('useSharedMenuItemCardState must be used within MenuItemCardProvider')
  }
  return context
}

type MenuItemCardProviderProps = {
  vendor: MenuVendor
  addSource?: ItemAddSource
  children: React.ReactNode
}

export const MenuItemCardProvider: React.FC<MenuItemCardProviderProps> = ({
  vendor,
  addSource = 'SupplierMenu',
  children,
}) => {
  const searchParams = useSearchParams()
  const language = useLanguage()
  const authState = useAuthState()
  const { t } = useTranslation()
  const { trackBasketCreated } = useMixpanelBasketCreated()

  const orderIdParam = searchParams?.get('orderId') || ''
  const basketIdParam = searchParams?.get('id') || searchParams?.get('basketId') || ''
  const standingOrderIdParam = searchParams?.get('standingOrderId') || ''
  const preventAutomaticBasketSelection = Boolean(searchParams?.get('preventAutoBasketSelection'))

  const { setOrderDetailsModalState } = useCartModalActions()

  const isEditingExistingOrder = Boolean(orderIdParam)
  const isEditingStandingOrder = Boolean(standingOrderIdParam)

  const account = useMemo(
    () =>
      match(authState)
        .with({ type: 'authenticated' }, ({ user }) => user.account)
        .otherwise(() => null),
    [authState],
  )

  const { selectedVendorLocation, deliveryFee, minOrderValueExTax } = useVendorLocationData()

  const { set: setUserBasketSettings, value: userBasketSettingsValue } =
    useUserBasketSettingsState()

  const { selectedBasket, createBasket } = useBasketSelection(
    {
      isAuthenticatedUser: authState.type === 'authenticated',
      accountId:
        authState.type === 'authenticated' ? (authState.user.account?.id ?? undefined) : undefined,
      vendorId:
        authState.type !== 'loading' && !isEditingExistingOrder && !isEditingStandingOrder
          ? vendor.id
          : undefined,
      initialSelectedId: basketIdParam,
      preventAutomaticSelection: preventAutomaticBasketSelection,
    },
    { syncConfig: false },
  )

  const existingOrderState = useExistingOrder()

  const { basket: basketData, addItemToBasket } = useSelectedBasketState({
    basketId: selectedBasket?.id ?? null,
    language,
    standingOrderId: standingOrderIdParam,
    deliveryFee,
    minOrderValueExTax,
    onBasketDataChange: useCallback(
      (newBasketData: BasketFragment) => {
        if (isEditingExistingOrder || isEditingStandingOrder) return
        const d = parseISO(newBasketData.settings.deliveryDate)
        const dateMs = isValidDate(d) ? d.getTime() : undefined
        setUserBasketSettings({
          location: newBasketData.settings.location || undefined,
          date: dateMs,
          time: newBasketData.settings.deliveryTimeSlot,
          headCount: newBasketData.settings.headCount,
        })
      },
      [isEditingExistingOrder, isEditingStandingOrder, setUserBasketSettings],
    ),
  })

  const { addingItem: sharedAddingItem } = useBasketStateStore()

  const { trackItemAddedToBasket } = useMixpanelItemAddedToBasket({
    vendor,
    orderSettings: userBasketSettingsValue,
  })

  const trackAdd = useCallback(
    (payload: AddItemPayload) => {
      if (!payload.item) return
      trackItemAddedToBasket({
        itemId: payload.item._id,
        itemName: payload.item.name,
        quantity: payload.qty,
        source: addSource,
        isCateringTeamOrder: basketData?.isCateringTeamOrder,
      })
    },
    [trackItemAddedToBasket, addSource, basketData?.isCateringTeamOrder],
  )

  // Create basket handler - accepts vendor to support multiple vendors if needed
  const handleCreateBasket = useCallback(
    (itemVendor: MenuVendor, itemPayload?: AddItemPayload) => {
      const availabilityId = itemVendor.gmAvailability?.id ?? null
      const vendorLocationId = selectedVendorLocation?.id ?? null
      const vendorLocationValid = Boolean(selectedVendorLocation)
      const validatedBasketSettings = validateUserBasketSettings(userBasketSettingsValue)

      if (!vendorLocationValid || !availabilityId) {
        setOrderDetailsModalState({
          open: true,
          createOnConfirm: true,
          itemToAddOnCreate: itemPayload,
          itemToAddSource: addSource,
        })
        return
      }

      if (basketData && itemPayload) {
        addItemToBasket(basketData, itemPayload)
        trackAdd(itemPayload)
        return
      }

      if (validatedBasketSettings.type === 'success') {
        createBasket({
          vendor: itemVendor,
          vendorLocationId,
          account,
          availabilityId,
          itemPayload,
          userBasketSettings: validatedBasketSettings.value,
          onBasketCreated: () => {
            trackBasketCreated({
              vendor: {
                id: itemVendor.id,
                companyName: itemVendor.companyName,
                isPantry: itemVendor.isPantry,
              },
              orderSettings: userBasketSettingsValue,
            })
            if (itemPayload) trackAdd(itemPayload)
          },
        })
        return
      }

      setOrderDetailsModalState({
        open: true,
        createOnConfirm: true,
        itemToAddOnCreate: itemPayload,
        itemToAddSource: addSource,
      })
    },
    [
      selectedVendorLocation,
      userBasketSettingsValue,
      basketData,
      setOrderDetailsModalState,
      addItemToBasket,
      createBasket,
      account,
      trackBasketCreated,
      trackAdd,
      addSource,
    ],
  )

  // Add item to basket handler
  const handleAddItemToBasket = useCallback(
    (itemVendor: MenuVendor, payload: AddItemPayload) => {
      if (isEditingExistingOrder) {
        const live = payload.item
        if (!live) return

        const customerPriceItem = getCustomerFacingPriceItem(live.customerPriceItems)

        existingOrderState.addNewItemToOrder({
          liveItemId: live._id,
          variationId: live.variationId,
          name: live.name,
          priceExTax: customerPriceItem.priceExTax,
          priceIncTax: customerPriceItem.price,
          qty: payload.qty,
          taxRate: live.taxRate,
          note: payload.note ?? undefined,
          minimumQuantity: live.minQty ?? 1,
          options:
            payload.options?.map((opt) => {
              const customerPriceItemOption = getCustomerFacingPriceItem(
                opt.optionItem.customerPriceItems,
              )
              return {
                id: opt.optionItem._id,
                name: opt.optionItem.item.name,
                optionCategoryId: opt.option._id,
                priceExTax: customerPriceItemOption.priceExTax,
                priceIncTax: customerPriceItemOption.price,
                qty: opt.qty * payload.qty,
                taxRate: opt.optionItem.taxRate,
                optionType: opt.option.optionType || '',
              }
            }) ?? [],
        })
        return
      }

      if (basketData) {
        addItemToBasket(basketData, payload)
        trackAdd(payload)
      } else {
        handleCreateBasket(itemVendor, payload)
      }
    },
    [
      isEditingExistingOrder,
      basketData,
      existingOrderState,
      addItemToBasket,
      handleCreateBasket,
      trackAdd,
    ],
  )

  const value = useMemo<SharedMenuItemCardState>(
    () => ({
      authState,
      account,
      isEditingExistingOrder,
      isEditingStandingOrder,
      t,
      selectedVendorLocation,
      basketData,
      addItemToBasket,
      sharedAddingItem,
      setOrderDetailsModalState,
      handleCreateBasket,
      handleAddItemToBasket,
    }),
    [
      authState,
      account,
      isEditingExistingOrder,
      isEditingStandingOrder,
      t,
      selectedVendorLocation,
      basketData,
      addItemToBasket,
      sharedAddingItem,
      setOrderDetailsModalState,
      handleCreateBasket,
      handleAddItemToBasket,
    ],
  )

  return <MenuItemCardContext.Provider value={value}>{children}</MenuItemCardContext.Provider>
}
